/**
 * 跨客戶 / 跨廠商的帳款報表(REQ-20260815002)—— 舊 PC 會計系統「應收付款」選單裡
 * 那一疊表單。
 *
 * **全部是唯讀查詢,沒有任何一張表是新開的**:資料一律從
 * `sales_orders` / `purchase_orders`(單據)+ `ar_settlements` / `ap_settlements`
 * (沖帳)當場算出來,遵守 REQ-20260806002 §2.1 的決定 ——
 * 「單據是事實,餘額是算出來的」,不另開應收 / 應付帳款表當第三份真相。
 *
 * ⚠️ 舊系統這幾個表名很容易混淆,本次的定義寫死在這裡:
 *
 * | 名稱 | 一列是什麼 | 講的是哪個時間點 |
 * |---|---|---|
 * | **明細表** | 一張未結單據 | **現在**還沒收 / 付完的每一張單 |
 * | ↳ 應收明細(`ar-detail`) | 同上 | 日期區間篩的是**完貨交貨日**,不是訂單日期(REQ-20260901008) |
 * | **簡要表** | 一個客戶 / 廠商 | 明細表以對象彙總的未結合計 |
 * | **彙總表** | 一個客戶 | 期初 → 本期發生 → 期末的**滾動**過程 |
 * | **統計表** | 一個月份 | 全體客戶 / 廠商在該月的發生額(不看餘額) |
 * | **帳齡分析** | 一個客戶 | 未結金額**依逾期天數**分桶 |
 *
 * 「簡要 / 彙總」的差別是**餘額 vs 過程**;「彙總 / 統計」的差別是**逐客戶 vs 逐月份**。
 */
import { z } from 'zod';

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD');

/**
 * 報表共用的可選日期區間。**兩端都可留白**:
 *  - 明細 / 簡要 / 帳齡 / 標籤:篩的是單據日期(`txnDate`),留白 = 不限
 *    (例外:應收明細 `ar-detail` 改篩**完貨交貨日**,見 `ArApDetailRow.deliveryDate`)
 *  - 統計 / 彙總:界定「本期」的起訖,留白時統計表回全部月份、
 *    彙總表把整段歷史當一期(期初就是客戶主檔的開帳金額)
 */
export const ReportRangeQuerySchema = z.object({
  from: ymd.optional(),
  to: ymd.optional(),
});
export type ReportRangeQuery = z.infer<typeof ReportRangeQuerySchema>;

/**
 * 業務員報表:可再篩單一業務員。
 *
 * `customerId` 是應收明細表專用的單一客戶篩選(REQ-20260824010)——
 * 兩個篩選互相獨立,都給就都要符合。留白時的語意與 `from` / `to` 一致:**不限**。
 */
export const SalesPersonReportQuerySchema = ReportRangeQuerySchema.extend({
  salesPersonId: z.coerce.number().int().positive().optional(),
  customerId: z.coerce.number().int().positive().optional(),
});
export type SalesPersonReportQuery = z.infer<typeof SalesPersonReportQuerySchema>;

/** 日報表:預設今天(台北),不是容器的 UTC 今天(CLAUDE.md §6.1) */
export const ReportDateQuerySchema = z.object({
  date: ymd.optional(),
});
export type ReportDateQuery = z.infer<typeof ReportDateQuerySchema>;

// ──────────────────────────────────────────────────────────────────────────
// 1. 應收 / 應付帳款明細表 —— 一列一張未結單據
// ──────────────────────────────────────────────────────────────────────────

/**
 * 明細列底下的品項(REQ-20260817003)—— 讓「依客戶分別列印」印得出逐品項的請款明細。
 *
 * 數量是 `DECIMAL(14,4)`,格式化時**不能**當金額四捨五入成整數。
 */
export interface ArApDetailItem {
  lineId: number;
  productCategoryName: string | null;
  productName: string;
  qty: string;
  unitName: string | null;
  /**
   * 單價(`sales_order_lines.unitPrice`),**原樣輸出**(REQ-20260831002)。
   * 顯示時**不可**套 `formatMoney` 四捨五入 —— 否則「數量 × 單價」會對不上金額
   * (與 REQ-20260815013 §三.3 同一條)。
   */
  unitPrice: string;
  /** 該品項的未稅金額(`sales_order_lines.amount`) */
  amount: string;
}

/**
 * 應收與應付共用同一個列型別:欄位語意一一對應(客戶↔廠商、已收↔已付),
 * 分成兩個型別只會讓前端把同一張表格寫兩次。
 * `salesPersonId` / `salesPersonName` 只有應收側有值,應付側一律 null。
 */
export interface ArApDetailRow {
  orderId: number;
  orderNo: string;
  txnDate: string;
  dueDate: string | null;
  /** 客戶 / 廠商 */
  partyId: number;
  partyCode: string | null;
  partyName: string;
  /** 未稅合計(含版費 / 刀模費);`total` 是含稅 */
  subtotal: string;
  total: string;
  settledAmount: string;
  allowanceAmount: string;
  outstanding: string;
  salesPersonId: number | null;
  salesPersonName: string | null;

  /**
   * 逐品項明細(REQ-20260817003)。**只有應收明細(`ar-detail` / `ar-by-person`)會帶**,
   * 應付側維持不填 —— 同 `salesPersonName` 只有應收側有值的既有模式。
   * 歷史 mdb 匯入單可以只有單頭沒有品項,那時是空陣列而不是缺這個欄位。
   */
  items?: ArApDetailItem[];

  /**
   * 請款抬頭要印的客戶資料(REQ-20260817003),同樣只有應收明細會帶。
   *
   * 逐列重複是刻意的:明細表的回應本來就是一張平的清單(`partyName` / `partyCode`
   * 也逐列重複),為了四個欄位多開一層 parties 陣列,前端就得多寫一段 join。
   */
  partyTaxId?: string | null;
  partyAddress?: string | null;
  partyContact?: string | null;
  partyPhone?: string | null;
  /**
   * 客戶全名與收款憑證(REQ-20260901013)。勾「依客戶分別列印」時每位客戶各印一份
   * 完整抬頭,而抬頭的「客戶名稱」要的是全名 —— `partyName` 是 `simpleName || fullName`
   * (畫面用的簡稱),兩者不是同一件事。收款憑證原本完全沒有帶。
   * 兩個都在 `customers` 表既有,只是多 select 兩格,沒有 migration。
   */
  partyFullName?: string | null;
  partyPaymentCert?: string | null;

  /**
   * 這張單的貨**交完了沒**(REQ-20260901008)。**只有應收明細(`ar-detail`)會帶**,
   * `ap-detail` / `ar-by-person` 維持不填(同 `items` / `partyTaxId` 的既有模式)。
   *
   * 請款認列以完貨交貨日為準:`true` 才列入請款明細與列印,
   * `false` 是「這個區間下了單但還沒交完」,前端另開一區顯示且不印。
   */
  delivered?: boolean;
  /**
   * 完貨日(`YYYY-MM-DD`)—— 該單所有品項都出滿時的**最後一次出貨日期**。
   *
   * `null` 有兩種情況,前端一律退回顯示訂單日期:
   *  - 尚未完貨(`delivered` 為 false)
   *  - 出貨紀錄上線(2026-08-15)前、且完全沒有出貨紀錄的舊單 —— 視為已交貨,
   *    但沒有可信的交貨日可填(見 service 的 `SHIPMENT_TRACKING_SINCE`)
   */
  deliveryDate?: string | null;
}

export interface ArApDetailReport {
  rows: ArApDetailRow[];
  /**
   * 底部總計列。應收明細(`ar-detail`)**只加總已交貨的列** ——
   * 那是要印出去跟客人請款的數字(REQ-20260901008)。
   */
  totalAmount: string;
  totalSettled: string;
  totalOutstanding: string;

  /**
   * 未交貨區的小計(REQ-20260901008)。同 `delivered`,**只有 `ar-detail` 會帶**。
   * 與上面三個總計互斥:一張單只會落在其中一邊。
   */
  undeliveredAmount?: string;
  undeliveredOutstanding?: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 2. 應收 / 應付簡要表 —— 明細表以客戶 / 廠商為單位彙總
// ──────────────────────────────────────────────────────────────────────────

export interface PartySummaryRow {
  partyId: number;
  partyCode: string | null;
  partyName: string;
  /** 有未結餘額的單據張數 */
  orderCount: number;
  totalAmount: string;
  settledAmount: string;
  outstanding: string;
}

export interface PartySummaryReport {
  rows: PartySummaryRow[];
  totalAmount: string;
  totalSettled: string;
  totalOutstanding: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 3. 業務員應收統計 —— 明細表以業務員為單位彙總
// ──────────────────────────────────────────────────────────────────────────

export interface SalesPersonSummaryRow {
  /** null = 未指定業務員(歷史單匯入時一律 null,見 REQ-20260815002) */
  salesPersonId: number | null;
  salesPersonName: string;
  orderCount: number;
  totalAmount: string;
  outstanding: string;
}

export interface SalesPersonSummaryReport {
  rows: SalesPersonSummaryRow[];
  totalAmount: string;
  totalOutstanding: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 4. 應收 / 應付帳款統計表 —— 一列一個月份,講的是「發生額」不是餘額
// ──────────────────────────────────────────────────────────────────────────

export interface MonthlyStatRow {
  /** YYYY-MM */
  period: string;
  /** 本期出貨 / 本期進貨(單據含稅總額) */
  amount: string;
  /** 本期折讓 */
  allowance: string;
  /** 本期收款 / 本期付款(沖帳金額,依沖帳日期分月) */
  settled: string;
  orderCount: number;
}

export interface MonthlyStatReport {
  rows: MonthlyStatRow[];
  totalAmount: string;
  totalAllowance: string;
  totalSettled: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 5. 收款 / 付款沖銷日報表 —— 某一天建立的沖帳紀錄
// ──────────────────────────────────────────────────────────────────────────

export interface SettlementDailyRow {
  settlementId: number;
  documentNo: string;
  settledAt: string;
  partyId: number;
  partyName: string;
  cashAmount: string | null;
  checkAmount: string | null;
  /**
   * 匯款金額(REQ-20260817006)。**只有收款(應收)側有** ——
   * `ap_settlements` 沒有這個欄位,付款日報表拿不到值(undefined),
   * 那張表也不顯示這一欄。
   */
  transferAmount?: string | null;
  usePrepaid: string | null;
  totalSettled: string;
  /** 這筆沖帳沖到的單號,逗號串接給報表一眼看完 */
  sourceDocNos: string[];
  source: string;
}

export interface SettlementDailyReport {
  /** 實際查的那一天(沒帶參數時是台北的今天) */
  date: string;
  rows: SettlementDailyRow[];
  totalSettled: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 6. 應收帳款彙總表 —— 一列一個客戶的期初 → 期末滾動
// ──────────────────────────────────────────────────────────────────────────

export interface ArOverviewRow {
  customerId: number;
  customerCode: string | null;
  customerName: string;
  openingBalance: string;
  periodSales: string;
  periodAllowance: string;
  periodPayments: string;
  closingBalance: string;
}

export interface ArOverviewReport {
  rows: ArOverviewRow[];
  totalOpening: string;
  totalSales: string;
  totalAllowance: string;
  totalPayments: string;
  totalClosing: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 7. 應收帳款帳齡分析
// ──────────────────────────────────────────────────────────────────────────

/**
 * 分桶依據是**逾期天數** = 今天(台北)- 到期日,到期日沒填時退而用單據日期。
 * `notDue` 收的是逾期天數 ≤ 0 的(還沒到期,含今天到期)。
 */
export interface AgingRow {
  customerId: number;
  customerCode: string | null;
  customerName: string;
  notDue: string;
  days1to30: string;
  days31to60: string;
  days61to90: string;
  over90: string;
  total: string;
}

export interface AgingReport {
  /** 計算基準日(台北今天) */
  asOf: string;
  rows: AgingRow[];
  totalNotDue: string;
  totalDays1to30: string;
  totalDays31to60: string;
  totalDays61to90: string;
  totalOver90: string;
  totalOutstanding: string;
}

// ──────────────────────────────────────────────────────────────────────────
// 8. 應收帳款郵寄標籤
// ──────────────────────────────────────────────────────────────────────────

export interface MailingLabelRow {
  customerId: number;
  customerCode: string | null;
  customerName: string;
  /** 送貨地址(沒有就退回發票地址 / 主地址) */
  address: string | null;
  taxId: string | null;
  contact: string | null;
  phone: string | null;
  mobile: string | null;
  outstanding: string;
}

export interface MailingLabelReport {
  rows: MailingLabelRow[];
}
